import { decodeJwtPayload } from './decodeJwtPayload';
import { GALLERY_ID_STORAGE_KEY } from '../galleryStorage';
import {
  clearPreviewSessionToken,
  getGuestAuthToken,
  hasPreviewSessionToken,
} from './guestToken';

const GUEST_AUTH_FAILURE_STORAGE_KEY = 'weddingGuestAuthFailure';
export const GUEST_AUTHENTICATED_EVENT = 'guest-authenticated';
const AUTH_FAILURE_MESSAGES = {
  expired: 'Session expired — enter the invite code again.',
  rotated: 'The invite code has changed — enter the current invite code to continue.',
  denied: 'This session is no longer valid for this gallery — enter the invite code again.',
};

/**
 * Path to open after guest JWT is cleared (e.g. 401). Prefer gallery entry for re-invite.
 */
export function getPathAfterGuestAuthFailure(galleryId) {
  const trimmed =
    galleryId !== undefined && galleryId !== null ? String(galleryId).trim() : '';
  if (trimmed) {
    return `/g/${encodeURIComponent(trimmed)}`;
  }
  return '/';
}

function responseMessage(error) {
  const data = error?.response?.data;
  if (typeof data === 'string') {
    return data;
  }
  if (data && typeof data.message === 'string') {
    return data.message;
  }
  return '';
}

function requestAuthorization(config) {
  const headers = config?.headers;
  return (
    headers?.Authorization ||
    headers?.authorization ||
    headers?.get?.('Authorization') ||
    ''
  );
}

function authorizationToken(authorization) {
  const raw = typeof authorization === 'string' ? authorization : '';
  return raw.startsWith('Bearer ') ? raw.slice(7).trim() : raw.trim();
}

function tokenGalleryId(authorization) {
  const token = authorizationToken(authorization);
  const payload = decodeJwtPayload(token);
  return typeof payload?.galleryId === 'string' ? payload.galleryId.trim() : '';
}

function guestRequestDetails(config) {
  if (!requestAuthorization(config)) {
    return null;
  }
  const url = typeof config?.url === 'string' ? config.url : '';
  const match = url.match(/\/galleries\/([^/?#]+)\/([^?#]*)/);
  if (!match || match[2] === 'auth' || match[2] === 'public-config') {
    return null;
  }
  try {
    return { galleryId: decodeURIComponent(match[1]) };
  } catch {
    return { galleryId: match[1] };
  }
}

function activeGuestGalleryIdentity(authorization) {
  const routeMatch = window.location.pathname.match(/^\/g\/([^/?#]+)/);
  let routeGalleryId = '';
  if (routeMatch) {
    try {
      routeGalleryId = decodeURIComponent(routeMatch[1]);
    } catch {
      routeGalleryId = routeMatch[1];
    }
  }
  const storedGalleryId = localStorage.getItem(GALLERY_ID_STORAGE_KEY) || '';
  const tokenGallery = tokenGalleryId(authorization);
  const ids = new Set([routeGalleryId].filter(Boolean));
  if (
    storedGalleryId &&
    (!routeGalleryId ||
      storedGalleryId === routeGalleryId ||
      storedGalleryId === tokenGallery)
  ) {
    ids.add(storedGalleryId);
  }
  return {
    routeGalleryId,
    storedGalleryId,
    ids,
  };
}

export function isActiveGuestGallery(galleryId, token) {
  const normalizedGalleryId =
    galleryId !== undefined && galleryId !== null ? String(galleryId).trim() : '';
  const activeGalleryIds = activeGuestGalleryIdentity(token).ids;
  return activeGalleryIds.size === 0 || activeGalleryIds.has(normalizedGalleryId);
}

export function getGuestAuthFailureReason(error) {
  const status = error?.response?.status;
  const request = guestRequestDetails(error?.config);
  if (!request) {
    return null;
  }
  const message = responseMessage(error).toLowerCase();
  if (status === 401) {
    return message.includes('re-enter') ? 'rotated' : 'expired';
  }
  if (
    status === 403 &&
    (message.includes('invalid token') ||
      message.includes('token gallery does not match') ||
      message.includes('invalid token role'))
  ) {
    return 'denied';
  }
  return null;
}

export function getLocalGuestTokenFailureReason(token, galleryId) {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return 'denied';
  }
  if (typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now()) {
    return 'expired';
  }
  const tokenGalleryId =
    typeof payload.galleryId === 'string' ? payload.galleryId.trim() : '';
  const expectedGalleryId =
    galleryId !== undefined && galleryId !== null ? String(galleryId).trim() : '';
  if (!tokenGalleryId || (expectedGalleryId && tokenGalleryId !== expectedGalleryId)) {
    return 'denied';
  }
  return null;
}

export function getGuestAuthFailureMessage(reason) {
  return AUTH_FAILURE_MESSAGES[reason] || AUTH_FAILURE_MESSAGES.expired;
}

export function getSafeGuestReturnPath(galleryId, location = window.location) {
  const galleryPath = getPathAfterGuestAuthFailure(galleryId);
  const pathname = typeof location?.pathname === 'string' ? location.pathname : '';
  const dashboardPath = `${galleryPath}/dashboard`;
  if (pathname !== dashboardPath && !pathname.startsWith(`${dashboardPath}/`)) {
    return null;
  }
  const search = typeof location?.search === 'string' ? location.search : '';
  return `${pathname}${search}`;
}

export function getStoredGuestAuthFailure(galleryId) {
  try {
    const stored = JSON.parse(sessionStorage.getItem(GUEST_AUTH_FAILURE_STORAGE_KEY));
    if (!stored || typeof stored !== 'object') {
      return null;
    }
    if (galleryId && stored.galleryId !== galleryId) {
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

export function clearStoredGuestAuthFailure() {
  sessionStorage.removeItem(GUEST_AUTH_FAILURE_STORAGE_KEY);
}

export function invalidateGuestSession({ galleryId, reason, returnTo } = {}) {
  const normalizedGalleryId =
    galleryId !== undefined && galleryId !== null ? String(galleryId).trim() : '';
  const existingFailure = getStoredGuestAuthFailure(normalizedGalleryId);
  const detail = {
    galleryId: normalizedGalleryId,
    reason: AUTH_FAILURE_MESSAGES[reason] ? reason : 'expired',
    returnTo:
      returnTo ||
      getSafeGuestReturnPath(normalizedGalleryId) ||
      existingFailure?.returnTo ||
      null,
  };
  if (hasPreviewSessionToken()) {
    // A rejected preview token ends the preview session only; the couple's own persisted
    // guest session (if any) is not theirs to clear.
    clearPreviewSessionToken();
  } else {
    localStorage.removeItem('weddingToken');
  }
  sessionStorage.setItem(GUEST_AUTH_FAILURE_STORAGE_KEY, JSON.stringify(detail));
  window.dispatchEvent(new CustomEvent('auth-error', { detail }));
  return detail;
}

export function recoverGuestApiAuthFailure(error, fallbackGalleryId) {
  if (error?.guestAuthFailure) {
    return error.guestAuthFailure;
  }
  const reason = getGuestAuthFailureReason(error);
  if (!reason) {
    return null;
  }
  const requestToken = authorizationToken(requestAuthorization(error.config));
  const currentToken = getGuestAuthToken() || '';
  if (currentToken && requestToken && currentToken !== requestToken) {
    return null;
  }
  const requestGalleryId = guestRequestDetails(error.config)?.galleryId;
  const activeGallery = activeGuestGalleryIdentity(requestAuthorization(error.config));
  if (!requestGalleryId || !activeGallery.ids.has(requestGalleryId)) {
    return null;
  }
  const detail = invalidateGuestSession({
    galleryId:
      fallbackGalleryId ||
      activeGallery.routeGalleryId ||
      activeGallery.storedGalleryId ||
      requestGalleryId,
    reason,
  });
  error.guestAuthFailure = detail;
  return detail;
}
